import React, { useCallback, useEffect, useState } from "react";
import { DatePicker, Spin, Table, Tabs, Tag } from "antd";
import PageContainerHeader from "../../molecules/PageContainerHeader/PageContainerHeader";
import EmployeeSelect from "../../atoms/EmployeeSelect/EmployeeSelect";
import {
  fetchTimeTrackerAdminHistory,
  fetchTimeTrackerAdminStatus,
} from "../../../app/utils/timeTracker/timeTrackerApi";
import { showErrorResponse } from "../../../app/utils/common/Functions";
import { formatDateTimeIst, formatWorkingDuration, computeTodayTotalWorkingMinutes, nowIst, resolveSessionWorkingMinutes, todayIstDateStr } from "../../../app/utils/common/dateTimeIst";
import {
  formatActivityEventLabel,
  formatTrackedActivities,
  TRACKED_ACTIVITY_LABELS,
} from "../../../app/utils/timeTracker/timeTrackerActivityDisplay";
import "./TimeTrackerAdminPage.scss";

const LIVE_REFRESH_MS = 2 * 60 * 1000;

const { RangePicker } = DatePicker;

const TAB_LIVE = "live";
const TAB_HISTORY = "history";

const formatDateTime = formatDateTimeIst;

const formatDuration = (row) => {
  const minutes =
    row?.todayTotalWorkingMinutes != null
      ? row.todayTotalWorkingMinutes
      : resolveSessionWorkingMinutes(row);
  return minutes != null ? formatWorkingDuration(minutes, { fromMinutes: true }) : "—";
};

const statusTag = (status) => {
  const normalized = String(status || "Offline");
  if (normalized === "Online") return <Tag color="green">Online</Tag>;
  if (normalized === "Idle") return <Tag color="orange">Idle</Tag>;
  if (normalized === "Paused") return <Tag color="gold">Paused</Tag>;
  return <Tag>Offline</Tag>;
};

const formatName = (row) =>
  `${row?.firstname || ""} ${row?.lastname || ""}`.trim() || `User #${row?.userId}`;

const enrichWithTodayTotals = (rows, todaySessions) => {
  const sessionsByUser = {};
  (todaySessions || []).forEach((session) => {
    if (!sessionsByUser[session.userId]) sessionsByUser[session.userId] = [];
    sessionsByUser[session.userId].push(session);
  });

  return (rows || []).map((row) => {
    if (row.todayTotalWorkingMinutes != null) return row;

    const userSessions = sessionsByUser[row.userId] || [];
    const liveMinutes = row.sessionId
      ? resolveSessionWorkingMinutes({
          id: row.sessionId,
          status: "active",
          checkInAt: row.checkInAt,
          checkOutAt: row.checkOutAt,
          isPaused: row.isPaused,
          pausedAt: row.pausedAt,
          totalPausedMinutes: row.totalPausedMinutes,
          totalWorkingMinutes: row.totalWorkingMinutes,
        })
      : null;

    return {
      ...row,
      todayTotalWorkingMinutes: computeTodayTotalWorkingMinutes(userSessions, {
        liveMinutes,
        activeSessionId: row.sessionId,
      }),
    };
  });
};

const TimeTrackerAdminPage = () => {
  const [activeTab, setActiveTab] = useState(TAB_LIVE);
  const [statusLoading, setStatusLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [dateRange, setDateRange] = useState([
    nowIst().subtract(7, "days"),
    nowIst(),
  ]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const today = todayIstDateStr();
      const [rows, todaySessions] = await Promise.all([
        fetchTimeTrackerAdminStatus(),
        fetchTimeTrackerAdminHistory({ from: today, to: today, limit: 500 }),
      ]);
      setEmployees(enrichWithTodayTotals(rows, todaySessions));
    } catch (error) {
      showErrorResponse(error);
      setEmployees([]);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const from = dateRange?.[0]?.format("YYYY-MM-DD");
      const to = dateRange?.[1]?.format("YYYY-MM-DD");
      const sessions = await fetchTimeTrackerAdminHistory({
        userId: selectedUserId,
        from,
        to,
        limit: 200,
      });
      setHistory(sessions);
    } catch (error) {
      showErrorResponse(error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [dateRange, selectedUserId]);

  useEffect(() => {
    if (activeTab !== TAB_LIVE) return undefined;
    loadStatus();
    const interval = setInterval(loadStatus, LIVE_REFRESH_MS);
    return () => clearInterval(interval);
  }, [activeTab, loadStatus]);

  useEffect(() => {
    if (activeTab !== TAB_HISTORY) return;
    loadHistory();
  }, [activeTab, loadHistory]);

  const statusColumns = [
    {
      title: "Employee",
      key: "name",
      render: (_, row) => formatName(row),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || "—",
    },
    {
      title: "Status",
      dataIndex: "currentStatus",
      key: "currentStatus",
      render: (status) => statusTag(status),
    },
    {
      title: "Check In",
      dataIndex: "checkInAt",
      key: "checkInAt",
      render: (value) => formatDateTime(value),
    },
    {
      title: "Check Out",
      dataIndex: "checkOutAt",
      key: "checkOutAt",
      render: (value) => formatDateTime(value),
    },
    {
      title: "Tracked Activity",
      key: "trackedActivity",
      render: (_, row) => {
        if (!row.sessionId) return "—";
        const activities = formatTrackedActivities(row.trackedActivities);
        const detectedTypes = new Set(activities.map((item) => item.type));
        return (
          <div className="time-tracker-admin-page__activity-tags">
            {Object.keys(TRACKED_ACTIVITY_LABELS).map((type) => (
              <Tag
                key={type}
                color={detectedTypes.has(type) ? "blue" : "default"}
                className={
                  detectedTypes.has(type) ? "" : "time-tracker-admin-page__activity-tag--inactive"
                }
              >
                {TRACKED_ACTIVITY_LABELS[type]}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Last Activity",
      dataIndex: "lastActivityAt",
      key: "lastActivityAt",
      render: (value) => formatDateTime(value),
    },
    {
      title: "Last Event",
      key: "lastActivityEvent",
      render: (_, row) => {
        if (!row.sessionId || !row.lastActivityEvent) return "—";
        return (
          <Tag color="geekblue">{formatActivityEventLabel(row.lastActivityEvent)}</Tag>
        );
      },
    },
    {
      title: "Working Hours (Today)",
      key: "hours",
      render: (_, row) => formatDuration(row),
    },
  ];

  const historyColumns = [
    {
      title: "Employee",
      key: "name",
      render: (_, row) => formatName(row),
    },
    {
      title: "Check In",
      dataIndex: "checkInAt",
      key: "checkInAt",
      render: (value) => formatDateTime(value),
    },
    {
      title: "Check Out",
      dataIndex: "checkOutAt",
      key: "checkOutAt",
      render: (value) => formatDateTime(value),
    },
    {
      title: "Working Hours",
      key: "hours",
      render: (_, row) => formatDuration(row),
    },
    {
      title: "Attendance",
      dataIndex: "attendanceStatus",
      key: "attendanceStatus",
      render: (value) => <Tag color="blue">{value || "present"}</Tag>,
    },
    {
      title: "Session Status",
      dataIndex: "status",
      key: "status",
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: "Last Activity",
      dataIndex: "lastActivityAt",
      key: "lastActivityAt",
      render: (value) => formatDateTime(value),
    },
  ];

  const tabItems = [
    {
      key: TAB_LIVE,
      label: "Live Employee Status",
      children: (
        <>
          <p className="time-tracker-admin-page__live-hint text-sm text-gray-500 mb-3">
            Auto-refreshes every 2 minutes. Tracked activity shows mouse, keyboard, click,
            and scroll events detected during the active session.
          </p>
          <Spin spinning={statusLoading}>
          <Table
            rowKey="userId"
            columns={statusColumns}
            dataSource={employees}
            pagination={{ pageSize: 15 }}
            scroll={{ x: true }}
          />
          </Spin>
        </>
      ),
    },
    {
      key: TAB_HISTORY,
      label: "Attendance History",
      children: (
        <>
          <div className="time-tracker-admin-page__history-toolbar">
            <EmployeeSelect
              value={selectedUserId}
              onSelectEmployee={setSelectedUserId}
              placeholder="All employees"
              allowClear
            />
            <RangePicker
              value={dateRange}
              onChange={(values) =>
                setDateRange(values || [nowIst().subtract(7, "days"), nowIst()])
              }
              allowClear={false}
            />
          </div>
          <Spin spinning={historyLoading}>
            <Table
              rowKey="id"
              columns={historyColumns}
              dataSource={history}
              pagination={{ pageSize: 20 }}
              scroll={{ x: true }}
            />
          </Spin>
        </>
      ),
    },
  ];

  return (
    <div className="time-tracker-admin-page page-container">
      <PageContainerHeader title="Time Tracker — Admin" />

      <div className="card-border card-body time-tracker-admin-page__tabs-wrap">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="time-tracker-admin-page__tabs"
        />
      </div>
    </div>
  );
};

export default TimeTrackerAdminPage;
